import React, { Fragment, ReactElement, useContext, useEffect, useState } from 'react';

import currencyFormatter from 'currency-formatter';
import { connect } from 'react-redux';
import { AuthContext, type AuthContextValue } from '~/auth';

import { Alert, Box, Stack, TextField, Typography } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import {
  applyLeCredit,
  applyPromoCode,
  emptyCart,
  purchaseFailure,
  setPromoCodeError,
  updateCredit,
  updateOrderNotes,
  updateReservation,
} from '~/actions/cart';

import { BOOKING_INFORMATION } from '~/consts/notes';
import { ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_OFFER, ITEM_TYPE_OFFLINE_FLIGHT } from '~/consts/order';
import { REASON_FOR_THE_WAIVED_SURCHARGE } from '~/consts/reservation';

import useAppNavigation from '~/hooks/useAppNavigation';

import { addOrderNote, getOrder, getOrderCombinedItemIds } from '~/services/OrdersService';
import {
  createLeCreditTransaction,
  createPromoTransaction,
  createStripePaymentIntent,
  createStripePaymentPlan,
  createStripeTransaction,
  getCredits,
} from '~/services/PaymentsService';

import { isAdmin } from '~/utils/adminPermission';
import getStripeAmount from '~/utils/getStripeAmount';
import isUUID from '~/utils/isUUID';

import { InsuranceUpdateError } from '../../Users/Orders/UpdateInsurance/StepPayment';
import ErrorDisplay from '../ErrorDisplay';
import Spinner from '../Spinner';

import AttributionSource from './AttributionSource';
import CreditPayment from './CreditPayment';
import DefaultDataPlaceholder from './DefaultDataPlaceholder';
import FlightOrderSummaryList from './FlightOrderSummaryList';
import GiftCardOrderSummaryList from './GiftCardOrderSummaryList';
import LatitudePayment from './LatitudePayment';
import OrderAddonsSummary, { type AddonSummaryItem } from './OrderAddonsSummary';
import OrderInsuranceSummary from './OrderInsuranceSummary';
import OrderSummaryList from './OrderSummaryList';
import PaymentBreakdown from './PaymentBreakdown';
import PromoPayment from './PromoPayment';
import StripePayment from './StripePayment';
import UseGiftCard from './UseGiftCard';

const summaryListComponents = {
  [ITEM_TYPE_OFFER]: OrderSummaryList,
  [ITEM_TYPE_GIFT_CARD]: GiftCardOrderSummaryList,
  [ITEM_TYPE_INSURANCE]: null,
  [ITEM_TYPE_OFFLINE_FLIGHT]: FlightOrderSummaryList,
};

// TODO: move this into API? or @luxuryescapes/lib-regions?
const MIN_LATITUDE_PAYMENT = 999;

type CartAddonSummaryItem = App.CartAddon & Omit<AddonSummaryItem, 'qty'>;

type StripePaymentInfo = {
  amount: number;
  paymentMethodId: string;
  confirmPayment: (clientSecret: string) => Promise<void>;
};

function getPaymentPlanItemsFromOrder(order): Array<{ id_items: string; total: number; type: string }> {
  const orderData = order?.result ?? order;
  const itemGroups = [
    orderData?.accommodation_items,
    orderData?.addon_items,
    orderData?.flight_items,
    orderData?.offline_flight_items,
    orderData?.gift_card_items,
    orderData?.purchase_credit_items,
    orderData?.bedbank_items,
    orderData?.insurance_items,
    orderData?.tour_items,
    orderData?.merchandise_items,
    orderData?.items,
  ];

  return itemGroups
    .flatMap((items) => items ?? [])
    .map((item) => ({
      id_items: item.id ?? item.id_items,
      total: Number(item.total ?? item.price ?? item.amount ?? 0),
      type: item.type,
    }))
    .filter((item) => item.id_items && item.type);
}

function getPaymentPlanItems(order): Array<{ id_items: string; total: number; type: string }> {
  return order?.paymentPlanItems?.length ? order.paymentPlanItems : getPaymentPlanItemsFromOrder(order);
}

function toPaymentAmount(amount: number | string): number {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    throw new Error('Payment amount must be a valid number');
  }

  return numericAmount;
}

function getPaymentProductType(cart: App.Cart): string {
  if (cart.orderType) {
    return cart.orderType;
  }

  if (cart.insurance?.applied) {
    return ITEM_TYPE_INSURANCE;
  }

  throw new Error('Payment product type must be set');
}

async function createAdminStripePaymentPlan({
  order,
  orderId,
  cart,
  tenant,
  paymentInfo,
  combinedItemIds,
}: {
  order: any;
  orderId: string;
  cart: App.Cart;
  tenant: App.Tenant;
  paymentInfo: StripePaymentInfo;
  combinedItemIds: Array<string>;
}) {
  const { amounts, currencyCode, customer, promo, transactionKey, businessId } = cart;
  const payments = [];

  if (amounts.savedAmount > 0 && promo.id) {
    payments.push({
      amount: toPaymentAmount(amounts.savedAmount),
      currency: currencyCode,
      type: 'promo',
      external_ref: promo.id,
    });
  }

  if (amounts.creditPaymentAmount > 0) {
    payments.push({
      amount: toPaymentAmount(amounts.creditPaymentAmount),
      currency: currencyCode,
      type: 'credits',
    });
  }

  payments.push({
    amount: toPaymentAmount(amounts.grandTotal),
    currency: currencyCode,
    type: 'stripe',
  });

  await createStripePaymentPlan({
    payment_plan_key: transactionKey,
    currency: currencyCode,
    fk_customer: customer.id_member,
    fk_orders: orderId,
    brand: tenant.brand,
    ...(isUUID(businessId) && { business_id: businessId }),
    item_ids: combinedItemIds,
    items: getPaymentPlanItems(order),
    payments,
    isSpoofed: false,
  });

  const paymentIntent = await createStripePaymentIntent({
    amount: toPaymentAmount(paymentInfo.amount),
    currency: currencyCode,
    brand: tenant.brand,
    paymentMethodType: 'card',
    metadata: {
      orderId,
      isSavedCard: false,
      isDeposit: false,
      shouldUpdateDefaultPaymentMethod: false,
      isNewCard: true,
      shouldSaveCard: false,
      isDeferredPayment: false,
      isDepositBalance: false,
      paymentMethodId: paymentInfo.paymentMethodId,
      productType: getPaymentProductType(cart),
      region: cart.regionCode,
    },
  });

  const clientSecret = paymentIntent?.result?.clientSecret;

  if (!clientSecret) {
    throw new Error('Stripe payment intent did not return a client secret');
  }

  await paymentInfo.confirmPayment(clientSecret);
}

function getRegionByCurrencyCode(currencyCode, brand) {
  const regions = libRegions.getRegions(brand);

  return regions.find((item) => {
    return item.currencyCode == currencyCode;
  });
}

type Props = {
  tenant: App.Tenant;
  brand: App.Brands;
  cart: App.Cart;
  insurance: App.InsuranceState;
  heading: string;
  dataPlaceholder: ReactElement;
  handleOrder: (cart: App.Cart, offerSource, brand, insuranceState) => Promise<void>;
  onAfterPayment?: (cart: App.Cart, orderId: string) => Promise<unknown>;
  doUpdateLeCredit?: Utils.ActionDispatcher<typeof updateCredit>;
  doApplyLeCredit?: Utils.ActionDispatcher<typeof applyLeCredit>;
  doApplyPromoCode?: Utils.ActionDispatcher<typeof applyPromoCode>;
  doSetPromoCodeError?: Utils.ActionDispatcher<typeof setPromoCodeError>;
  doEmptyCart?: Utils.ActionDispatcher<typeof emptyCart>;
  doPurchaseFailure?: Utils.ActionDispatcher<typeof purchaseFailure>;
  doUpdateOrderNotes?: Utils.ActionDispatcher<typeof updateOrderNotes>;
  doUpdateReservation?: Utils.ActionDispatcher<typeof updateReservation>;
};

// <Payment /> is responsible only for making payments. Callers must inject
// behaviors for creating or updating an order, and optionally before/after
// hooks for payment.
function Payment(props: Props) {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { push } = useAppNavigation();
  const [transacting, setTransacting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offerSource, setOfferSource] = useState(props.tenant.brand);
  const [waiveSurchargeNote, setWaiveSurchargeNote] = useState('');
  const [error, setError] = useState<Error & { errors?: Array<Error> }>();

  const isAdminUser = isAdmin(user);

  useEffect(() => {
    if (!isDataPresent()) {
      setLoading(false);
      return;
    }

    refreshCredit();
  }, []);

  function isDataPresent() {
    const {
      cart: { insurance: insuranceQuoteResult, offers, items },
    } = props;

    return offers.length > 0 || items.length > 0 || insuranceQuoteResult.applied;
  }

  const isStripeEnabled = () => {
    const {
      cart,
      tenant: { brand },
    } = props;
    const { currencyCode } = cart;

    const currentRegion = getRegionByCurrencyCode(currencyCode, brand);
    return currentRegion.paymentMethods.includes('stripe');
  };

  const isLatitudeEnabled = () => {
    if (window.configs.LATITUDE_ENABLED !== 'on') {
      return false;
    }

    const {
      cart,
      tenant: { brand },
    } = props;
    const {
      currencyCode,
      bookDatesOnly,
      amounts: { grandTotal },
      orderType,
    } = cart;

    const currentRegion = getRegionByCurrencyCode(currencyCode, brand);
    return (
      !bookDatesOnly &&
      grandTotal >= MIN_LATITUDE_PAYMENT &&
      currentRegion.paymentMethods.includes('latitude') &&
      orderType !== ITEM_TYPE_OFFLINE_FLIGHT
    );
  };

  const shouldShowGiftCards = () => {
    const {
      cart: { orderType, currencyCode },
      tenant: { brand },
    } = props;

    return (
      libRegions.getPaymentMethodsByCurrencyCode(currencyCode, brand).includes('giftcard') &&
      ![ITEM_TYPE_OFFLINE_FLIGHT].includes(orderType)
    );
  };

  const refreshCredit = () => {
    const { cart, doUpdateLeCredit, tenant } = props;
    const { currencyCode, customer } = cart;

    return getCredits(customer.id_member, currencyCode, tenant.brand)
      .then((creditData) => {
        doUpdateLeCredit(currencyCode, parseFloat(creditData.metadata.available_credit).toFixed(2), null);

        setLoading(false);
      })
      .catch((err) => {
        doUpdateLeCredit(currencyCode, 0, err);
      });
  };

  const onPurchaseCompletion = (orderId) => {
    const { doEmptyCart } = props;

    push(`/purchases/${orderId}`);
    doEmptyCart();
  };

  const adminCanPayWithCredit = () => {
    return window.configs.SALES_TEAM_LEADERS?.split(',').includes(user?.email);
  };

  const canPayEntirelyWithLeCredit = () => {
    const {
      cart: { credit: cartCredit, amounts, orderType },
    } = props;

    if (orderType === ITEM_TYPE_OFFLINE_FLIGHT) {
      return false;
    }

    if (orderType === ITEM_TYPE_GIFT_CARD && !adminCanPayWithCredit()) {
      return false;
    }

    // If already paying partially with le credit, return false
    if (amounts.creditPaymentAmount > 0) {
      return false;
    }

    return cartCredit.balance >= amounts.grandTotal;
  };

  const canPayPartiallyWithLeCredit = () => {
    const {
      cart: { credit: cartCredit, amounts, orderType },
    } = props;

    if (orderType === ITEM_TYPE_OFFLINE_FLIGHT) {
      return false;
    }

    if (orderType === ITEM_TYPE_GIFT_CARD && !adminCanPayWithCredit()) {
      return false;
    }

    return cartCredit.balance > 0 && cartCredit.balance < amounts.grandTotal;
  };

  const canUsePromo = () => {
    const {
      cart: { orderType, bookDatesOnly },
    } = props;

    return ![ITEM_TYPE_GIFT_CARD, ITEM_TYPE_OFFLINE_FLIGHT].includes(orderType) && !bookDatesOnly;
  };

  const setOfferSourceHandler = (source) => {
    setOfferSource(source);
  };

  // paymentWillStart is invoked by payment options before the actual payment
  // begins.
  const paymentWillStart = (done) => {
    if (props.cart.items.some((item) => item.reservation?.waivedSurchargeAmount)) {
      let reason;

      do {
        reason = prompt(REASON_FOR_THE_WAIVED_SURCHARGE);
      } while (reason === '');

      if (reason) {
        setTransacting(true);
        setWaiveSurchargeNote(reason);
        done();
      }

      return;
    }

    setTransacting(true);
    done();
  };

  const createOrder = () => {
    const { cart, handleOrder, tenant, insurance } = props;

    return handleOrder(cart, offerSource, tenant.brand, insurance);
  };

  // handlePayment creates the order and transactions for each payment methods
  // in the current payment breakdown. Upon completion, it redirects to the
  // confirmation page
  const handlePayment = async (paymentType, paymentInfo, orderId) => {
    const { cart, onAfterPayment, tenant } = props;
    const { customer, currencyCode, amounts, promo, transactionKey } = cart;

    let order;
    let combinedItemIds;

    try {
      if (!orderId) {
        order = await createOrder();
        orderId = order.id;
        combinedItemIds = order.combinedItemIds;
      }

      // HACK: Will be revorked and removed upon refactoring of order
      // workflow and hiding payment service api behind order service
      if (!order) {
        order = await getOrder(orderId);
        combinedItemIds = getOrderCombinedItemIds(order);
      }

      if (!combinedItemIds?.length) {
        combinedItemIds = getPaymentPlanItems(order).map((item) => item.id_items);
      }

      if (order.status === 'abandoned') {
        throw 'Order has "abandoned" status';
      }

      const paymentRequired = {
        fk_orders: orderId,
        transaction_key: transactionKey,
        brand: tenant.brand,
      };

      if (paymentType !== 'stripe' && amounts.savedAmount > 0) {
        await createPromoTransaction({
          ...paymentRequired,
          promo_id: promo.id,
          fk_orders: orderId,
          amount: amounts.savedAmount,
          id_customer: customer.id_member,
          currency: currencyCode,
        });
      }

      let creditPaymentAmount = 0;
      if (paymentType === 'lecredit') {
        creditPaymentAmount = amounts.grandTotal;
      } else {
        creditPaymentAmount = amounts.creditPaymentAmount;
      }

      if (paymentType === 'stripe') {
        if (paymentInfo.confirmPayment) {
          await createAdminStripePaymentPlan({
            order,
            orderId,
            cart,
            tenant,
            paymentInfo,
            combinedItemIds,
          });
        } else {
          await createStripeTransaction({
            ...paymentRequired,
            ...paymentInfo,
            amount: getStripeAmount(amounts.grandTotal, currencyCode),
            item_ids: combinedItemIds,
          });
        }
      }

      if (paymentType !== 'stripe' && creditPaymentAmount > 0) {
        await createLeCreditTransaction({
          ...paymentRequired,
          amount: creditPaymentAmount,
          id_customer: customer.id_member,
          currency_code: currencyCode,
          comments: `For order ${orderId}`,
        });
      }

      if (onAfterPayment) {
        await onAfterPayment(cart, orderId);
      }

      if (waiveSurchargeNote) {
        await addOrderNote(orderId, {
          comment: 'Waived surcharge: ' + waiveSurchargeNote,
          comment_type: BOOKING_INFORMATION,
        });
      }

      setTransacting(false);
      setWaiveSurchargeNote('');
      onPurchaseCompletion(orderId);
    } catch (error) {
      console.warn('error payment', error);

      if (typeof error.errors == 'string') {
        error.errors = [new Error(error.errors)];
      }

      props.doPurchaseFailure();

      if (error instanceof InsuranceUpdateError) {
        // Block payment form in case of insurance update error
        setError(error);
        setTransacting(true);
        setWaiveSurchargeNote('');
      } else {
        setError(error);
        setTransacting(false);
        setWaiveSurchargeNote('');
      }
    }
  };

  const getGroupedAddons = (addons: Array<App.CartAddon>): Array<AddonSummaryItem> => {
    // We store each addon in cart as separate item.
    // In order to show actual quantity just group them by addonId

    const result: Record<string, AddonSummaryItem> = {};
    for (const addon of addons) {
      const summaryAddon = addon as CartAddonSummaryItem;

      if (result[summaryAddon.addonId]) {
        result[summaryAddon.addonId].qty += 1;
      } else {
        result[summaryAddon.addonId] = {
          ...summaryAddon,
          qty: 1,
        };
      }
    }

    return Object.values(result);
  };

  const {
    cart,
    brand,
    doApplyLeCredit,
    doApplyPromoCode,
    doSetPromoCodeError,
    dataPlaceholder,
    tenant,
    doUpdateReservation,
    heading,
  } = props;
  const {
    customer,
    currencyCode,
    credit: cartCredit,
    amounts,
    promo,
    bookDatesOnly,
    insurance: insuranceQuoteResult,
    orderType,
    offers,
    items,
    stateOfResidence,
  } = cart;
  const insurance = props.insurance;
  const SummaryList = summaryListComponents[cart.orderType];

  const dataPresent = isDataPresent();
  const showPayWithLeCreditButton = canPayEntirelyWithLeCredit();
  const showUseLeCreditCheckbox = canPayPartiallyWithLeCredit();
  const canUsePromoValue = canUsePromo();
  const showGiftCards = shouldShowGiftCards();
  const requiresPayment = amounts.grandTotal > 0;
  const showStripe = requiresPayment && isStripeEnabled();
  const showLatitude = requiresPayment && isLatitudeEnabled();
  const isSurchargeWaived = offers.some((o) => o.items.some((item) => item.reservation?.waivedSurchargeAmount));

  if (loading) {
    return <Spinner />;
  }

  if (!dataPresent) {
    return dataPlaceholder || <DefaultDataPlaceholder />;
  }

  return (
    <div className="payment" data-no-dark-override>
      {error && <ErrorDisplay message={error.message} />}
      {error && error.errors && error.errors.map((err, i) => <ErrorDisplay key={i} message={err.message} />)}

      {transacting && !error && <Alert severity="info">Transaction in progress</Alert>}

      <Stack direction="row" spacing={2} alignItems="start">
        <Box flexGrow={3}>
          <div className="order-summary">
            <h2>{heading}</h2>

            {offers.map((offer, i) => (
              <Fragment key={i}>
                {offer && <AttributionSource setOfferSource={setOfferSourceHandler} source={offerSource} />}

                {SummaryList && (
                  <SummaryList
                    stateOfResidence={stateOfResidence}
                    orderType={orderType}
                    offer={offer}
                    offerNumber={i + 1}
                    items={offer.items}
                    currencyCode={currencyCode}
                    bookDatesOnly={bookDatesOnly}
                    isAdmin={isAdminUser}
                    doUpdateReservation={doUpdateReservation}
                  />
                )}
                {offer.addons && offer.addons.length > 0 && (
                  <OrderAddonsSummary addons={getGroupedAddons(offer.addons)} currencyCode={currencyCode} />
                )}
              </Fragment>
            ))}

            {SummaryList && (
              <SummaryList
                orderType={orderType}
                offer={null}
                items={items}
                currencyCode={currencyCode}
                bookDatesOnly={bookDatesOnly}
                isAdmin={isAdminUser}
                doUpdateReservation={doUpdateReservation}
              />
            )}

            {insuranceQuoteResult.applied && (
              <OrderInsuranceSummary
                productName={insuranceQuoteResult.productName}
                total={insuranceQuoteResult.total}
                destinationCountries={insurance.destinationCountries}
                currencyCode={insurance.currencyCode}
                travellers={insurance.travellers}
                startDate={insurance.startDate}
                endDate={insurance.endDate}
              />
            )}

            <PaymentBreakdown
              currencyCode={currencyCode}
              subTotal={amounts.subTotal}
              savedAmount={amounts.savedAmount}
              creditPaymentAmount={amounts.creditPaymentAmount}
              grandTotal={amounts.grandTotal}
              promoDiscountValue={promo.discount}
              promoType={promo.type}
            />

            {showUseLeCreditCheckbox && (
              <div>
                <label>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      const checked = e.target.checked;

                      let creditAmount = 0;
                      if (checked) {
                        creditAmount = cartCredit.balance;
                      }
                      doApplyLeCredit(creditAmount);
                    }}
                  />
                  Use credit - balance
                  {currencyFormatter.format(cartCredit.balance, {
                    code: currencyCode,
                  })}
                </label>
              </div>
            )}

            <Stack mt={2} direction="column" spacing={2}>
              {canUsePromoValue && (
                <PromoPayment
                  onPromoCodeApply={doApplyPromoCode}
                  onPromoCodeError={doSetPromoCodeError}
                  error={promo.error}
                  disabled={transacting}
                  customerId={customer.id_member}
                  currencyCode={currencyCode}
                  brand={brand}
                  cartValue={cart.amounts.value}
                />
              )}

              {showGiftCards && <UseGiftCard onGiftCardRedeemed={refreshCredit} customerId={customer.id_member} />}
            </Stack>
          </div>
        </Box>

        <Box flexGrow={1}>
          <Stack direction="column" spacing={2}>
            {requiresPayment &&
              (showStripe ? (
                <StripePayment
                  paymentWillStart={paymentWillStart}
                  onPayment={handlePayment}
                  customerId={customer.id_member}
                  customerEmail={customer.email}
                  customerName={customer.fullName}
                  currencyCode={currencyCode}
                  regionCode={cart.regionCode}
                  packagePrice={cart.amounts.grandTotal}
                  tenant={tenant}
                  disabled={transacting}
                />
              ) : (
                <div>Stripe is not enabled for {currencyCode}.</div>
              ))}
            {showLatitude && (
              <div>
                <LatitudePayment
                  onPayment={handlePayment}
                  customerId={customer.id_member}
                  brand={tenant.brand}
                  cart={cart}
                  createOrder={createOrder}
                  shouldPromoptWaiveSurcharge={isSurchargeWaived}
                />
              </div>
            )}
            {showPayWithLeCreditButton && (
              <div>
                <CreditPayment paymentWillStart={paymentWillStart} onPayment={handlePayment} disabled={transacting} />
              </div>
            )}
          </Stack>

          <Box mt={4}>
            <Typography fontWeight={500}>Vendor Notes</Typography>
            <TextField
              className="order-notes-input"
              value={cart.notes}
              onChange={(e) => props.doUpdateOrderNotes(e.target.value)}
              minRows={2}
              multiline
              fullWidth
            />
          </Box>
        </Box>
      </Stack>
    </div>
  );
}

const mapStateToProps = (state: App.State) => ({
  cart: state.cart,
  insurance: state.insurance,
  brand: state.tenant.brand,
  tenant: state.tenant,
});

const mapDispatchToProps = {
  doUpdateLeCredit: updateCredit,
  doApplyLeCredit: applyLeCredit,
  doApplyPromoCode: applyPromoCode,
  doSetPromoCodeError: setPromoCodeError,
  doEmptyCart: emptyCart,
  doPurchaseFailure: purchaseFailure,
  doUpdateOrderNotes: updateOrderNotes,
  doUpdateReservation: updateReservation,
};

export default connect(mapStateToProps, mapDispatchToProps)(Payment);
